import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { StyledSearch, StyledSearchButton } from "../../styles/StyledSearch";
import SearchIcon from "../../assets/img/icons/search.svg";
import AdvancedSearch from "./AdvancedSearch";
import { getQuestionSuggestions } from "../../api/decision/questionSuggestions";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import styled, { ThemeProvider } from "styled-components";
import { theme } from "../../styles/theme";
import { QueryLoader } from "../Loader";

// Styled components for suggestions
const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  width: 100%;
  max-width: 57.4rem;
  background: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.blue1};
  border-top: none;
  border-radius: 0 0 0.6rem 0.6rem;
  box-shadow: 0 0.2rem 0.8rem rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 24rem;
  overflow-y: auto;
  font-family: ${({ theme }) => theme.font.secondary};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.8rem 1.2rem;
  background: ${({ theme }) => theme.color.background};
  border-bottom: 1px solid ${({ theme }) => theme.color.blue0};
  border-radius: 0 0 0 0;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.color.blue3};
`;

const SectionMeta = styled.span`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.color.gray};
`;

const SuggestionItem = styled.div`
  padding: 1rem 1.4rem;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.color.blue0};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 1rem;

  &:hover {
    background-color: ${({ theme }) => theme.color.background};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
  flex: 1 1 auto;
`;

const QuestionText = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.color.black};
  font-size: 1.4rem;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Right-aligned, explicit "From:" label
const RightInfo = styled.div`
  flex: 0 0 auto;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.color.blue3};
  font-weight: 500;
  white-space: nowrap;

  &::before {
    content: "From: ";
    color: ${({ theme }) => theme.color.gray};
    font-weight: 400;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  border-radius: 0 0 0.3rem 0.3rem;
`;

const SkeletonItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.4rem;
  opacity: 0.3;
`;

const SkeletonCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  width: 100%;
`;

const SkeletonBar = styled.div`
  height: ${({ h }) => h || "1.4rem"};
  width: ${({ w }) => w || "70%"};
  background: #eef2f7;
  border-radius: 0.3rem;
`;

// This container is used on the page, for general structure
const SearchContainer = styled.div`
  position: relative;
  width: fit-content;
`;

export default function Search({ 
  advancedSearch = false, 
  isWhite = false, 
  setPhrase 
}) {
  const { axiosPrivate } = useAxiosPrivate();
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Debounce function (no changes here)
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchTerm.length >= 2 && isInputFocused) {
        setIsLoading(true);
        try {
          const results = await getQuestionSuggestions(axiosPrivate, searchTerm);
          setSuggestions(results);
          setShowSuggestions(true);
        } catch (error) {
          console.error("Error fetching suggestions:", error);
          setSuggestions([]);
          setShowSuggestions(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, axiosPrivate, isInputFocused]);

  // Handle clicking outside (no changes here)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setIsInputFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // All handlers (no changes here)
  const handleSearchButton = (e) => {
    e.preventDefault();
    const value = e.currentTarget.previousSibling.value;
    setPhrase(value);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setPhrase(e.currentTarget.value);
      setShowSuggestions(false);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.currentTarget.value;
    setSearchTerm(value);

    if (value === "") {
      setPhrase(value);
    }
  };

  const handleSuggestionClick = (question) => {
    setSearchTerm(question);
    setShowSuggestions(false);
    setPhrase(question);

    if (inputRef.current) {
      inputRef.current.value = question;
      inputRef.current.focus();
    }
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
    if (suggestions.length > 0 && searchTerm.length >= 2) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setIsInputFocused(false);
      setShowSuggestions(false);
    }, 150);
  };

  return (
    <SearchContainer>
      <StyledSearch isWhite={isWhite}>
        <input
          ref={inputRef}
          type={"search"}
          placeholder="Search"
          data-testid="search-input"
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
        />
        <StyledSearchButton isWhite={isWhite} type="button" onClick={handleSearchButton}>
          <img src={SearchIcon} alt="search" />
        </StyledSearchButton>
        {advancedSearch ? <AdvancedSearch /> : undefined}

        {/* Move suggestions dropdown inside StyledSearch for proper positioning */}
        <ThemeProvider theme={theme}>
          {showSuggestions && isInputFocused && (
            <SuggestionsDropdown ref={suggestionsRef} role="listbox" aria-labelledby="suggested-questions-header">
              <SectionHeader>
                <SectionTitle id="suggested-questions-header">Suggested questions</SectionTitle>
                <SectionMeta>
                  {isLoading ? "Loading…" : `${suggestions.length} suggestion${suggestions.length === 1 ? "" : "s"}`}
                </SectionMeta>
              </SectionHeader>

              {suggestions.length > 0 && !isLoading &&
                suggestions.map((suggestion, index) => (
                  <SuggestionItem
                    key={`${suggestion.decisionId || index}-${suggestion.question}-${index}`}
                    role="option"
                    onClick={() => handleSuggestionClick(suggestion.question)}
                  >
                    <Left>
                      <QuestionText>{suggestion.question}</QuestionText>
                    </Left>
                    <RightInfo>{suggestion.decisionName}</RightInfo>
                  </SuggestionItem>
                ))}

              {/* Show placeholder suggestions when loading */}
              {isLoading && (
                <>
                  {suggestions.length > 0 ? (
                    suggestions.map((_, index) => (
                      <SkeletonItem key={`loading-${index}`}>
                        <SkeletonCol>
                          <SkeletonBar h="1.4rem" w="70%" />
                          <SkeletonBar h="1.2rem" w="40%" />
                        </SkeletonCol>
                      </SkeletonItem>
                    ))
                  ) : (
                    <>
                      <SkeletonItem>
                        <SkeletonCol>
                          <SkeletonBar h="1.4rem" w="70%" />
                          <SkeletonBar h="1.2rem" w="40%" />
                        </SkeletonCol>
                      </SkeletonItem>
                      <SkeletonItem>
                        <SkeletonCol>
                          <SkeletonBar h="1.4rem" w="65%" />
                          <SkeletonBar h="1.2rem" w="35%" />
                        </SkeletonCol>
                      </SkeletonItem>
                      <SkeletonItem>
                        <SkeletonCol>
                          <SkeletonBar h="1.4rem" w="60%" />
                          <SkeletonBar h="1.2rem" w="30%" />
                        </SkeletonCol>
                      </SkeletonItem>
                    </>
                  )}
                  <LoadingOverlay>
                    <QueryLoader />
                  </LoadingOverlay>
                </>
              )}

              {/* Show message when no suggestions found and not loading */}
              {!isLoading && suggestions.length === 0 && (
                <SuggestionItem>
                  <Left>
                    <QuestionText>No question suggestions</QuestionText>
                  </Left>
                </SuggestionItem>
              )}
            </SuggestionsDropdown>
          )}
        </ThemeProvider>
      </StyledSearch>
    </SearchContainer>
  );
}

Search.propTypes = {
  advancedSearch: PropTypes.bool,
  isWhite: PropTypes.bool,
  setPhrase: PropTypes.func,
};